// ----------------------------------------------------------------------

export function pxToRem(value: number) {
  return `${value / 16}rem`;
}

function responsiveFontSizes({ sm, md, lg }: { sm: number; md: number; lg: number }) {
  return {
    '@media (min-width:600px)': {
      fontSize: pxToRem(sm),
    },
    '@media (min-width:900px)': {
      fontSize: pxToRem(md),
    },
    '@media (min-width:1200px)': {
      fontSize: pxToRem(lg),
    },
  };
}

const FONT_PRIMARY = 'Public Sans, sans-serif'; // Google Font
// const FONT_SECONDARY = 'CircularStd, sans-serif'; // Local Font

const typography = {
  fontFamily: FONT_PRIMARY,
  fontWeightRegular: 400,
  fontWeightMedium: 600,
  fontWeightBold: 700,
  h1: {
    fontWeight: 700,
    lineHeight: 80 / 64,
    fontSize: pxToRem(40),
    ...responsiveFontSizes({ sm: 52, md: 58, lg: 64 }),
  },
  h2: {
    fontWeight: 700,
    lineHeight: 64 / 48,
    fontSize: pxToRem(32),
    ...responsiveFontSizes({ sm: 40, md: 44, lg: 48 }),
  },
  h3: {
    fontWeight: 700,
    lineHeight: 1.5,
    fontSize: pxToRem(24),
    ...responsiveFontSizes({ sm: 26, md: 30, lg: 32 }),
  },
  h4: {
    fontWeight: 700,
    lineHeight: 1.5,
    fontSize: pxToRem(20),
    ...responsiveFontSizes({ sm: 20, md: 24, lg: 24 }),
  },
  h5: {
    fontWeight: 700,
    lineHeight: 1.5,
    fontSize: pxToRem(18),
    ...responsiveFontSizes({ sm: 19, md: 20, lg: 20 }),
  },
  h6: {
    fontWeight: 700,
    lineHeight: 28 / 18,
    fontSize: pxToRem(17),
    ...responsiveFontSizes({ sm: 18, md: 18, lg: 18 }),
  },
  subtitle1: {
    fontWeight: 600,
    lineHeight: 1.5,
    fontSize: pxToRem(16),
  },
  subtitle2: {
    fontWeight: 600,
    lineHeight: 22 / 14,
    fontSize: pxToRem(14),
  },
  body1: {
    lineHeight: 1.5,
    fontSize: pxToRem(16),
  },
  body2: {
    lineHeight: 22 / 14,
    fontSize: pxToRem(14),
  },
  caption: {
    lineHeight: 1.5,
    fontSize: pxToRem(12),
  },
  overline: {
    fontWeight: 700,
    lineHeight: 1.5,
    fontSize: pxToRem(12),
    letterSpacing: 1.1,
    textTransform: 'uppercase',
  },
  button: {
    fontWeight: 700,
    lineHeight: 24 / 14,
    fontSize: pxToRem(14),
    textTransform: 'capitalize',
  },

  // [Kor] type scale ---------------------------------------------------------------------------------------------------------------
  Kor_28_b: { fontWeight: 700, lineHeight: pxToRem(38), fontSize: pxToRem(28), wordBreak: 'keep-all' },
  Kor_28_r: { fontWeight: 400, lineHeight: pxToRem(38), fontSize: pxToRem(28), wordBreak: 'keep-all' },
  Kor_22_b: { fontWeight: 700, lineHeight: pxToRem(32), fontSize: pxToRem(22), wordBreak: 'keep-all' },
  Kor_22_m: { fontWeight: 500, lineHeight: pxToRem(32), fontSize: pxToRem(22), wordBreak: 'keep-all' },
  Kor_22_r: { fontWeight: 400, lineHeight: pxToRem(32), fontSize: pxToRem(22), wordBreak: 'keep-all' },
  Kor_20_b: { fontWeight: 700, lineHeight: pxToRem(30), fontSize: pxToRem(20), wordBreak: 'keep-all' },
  Kor_20_r: { fontWeight: 400, lineHeight: pxToRem(30), fontSize: pxToRem(20), wordBreak: 'keep-all' },
  Kor_18_b: { fontWeight: 700, lineHeight: pxToRem(26), fontSize: pxToRem(18), wordBreak: 'keep-all' },
  Kor_18_m: { fontWeight: 500, lineHeight: pxToRem(26), fontSize: pxToRem(18), wordBreak: 'keep-all' },
  Kor_18_r: { fontWeight: 400, lineHeight: pxToRem(26), fontSize: pxToRem(18), wordBreak: 'keep-all' },
  Kor_18_sb: { fontWeight: 600, lineHeight: pxToRem(26), fontSize: pxToRem(18), wordBreak: 'keep-all' },
  Kor_16_b: { fontWeight: 700, lineHeight: pxToRem(24), fontSize: pxToRem(16), wordBreak: 'keep-all' },
  Kor_16_r: { fontWeight: 400, lineHeight: pxToRem(24), fontSize: pxToRem(16), wordBreak: 'keep-all' },
  Kor_14_b: { fontWeight: 700, lineHeight: pxToRem(24), fontSize: pxToRem(14), wordBreak: 'keep-all' },
  Kor_14_r: { fontWeight: 400, lineHeight: pxToRem(24), fontSize: pxToRem(14), wordBreak: 'keep-all' },
} as const;

declare module '@mui/material/styles' {
  interface TypographyVariants {
    Kor_28_b: React.CSSProperties;
    Kor_28_r: React.CSSProperties;
    Kor_22_b: React.CSSProperties;
    Kor_22_m: React.CSSProperties;
    Kor_22_r: React.CSSProperties;
    Kor_20_b: React.CSSProperties;
    Kor_20_r: React.CSSProperties;
    Kor_18_b: React.CSSProperties;
    Kor_18_m: React.CSSProperties;
    Kor_18_r: React.CSSProperties;
    Kor_18_sb: React.CSSProperties;
    Kor_16_b: React.CSSProperties;
    Kor_16_r: React.CSSProperties;
    Kor_14_b: React.CSSProperties;
    Kor_14_r: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    Kor_28_b: React.CSSProperties;
    Kor_28_r: React.CSSProperties;
    Kor_22_b: React.CSSProperties;
    Kor_22_m: React.CSSProperties;
    Kor_22_r: React.CSSProperties;
    Kor_20_b: React.CSSProperties;
    Kor_20_r: React.CSSProperties;
    Kor_18_b: React.CSSProperties;
    Kor_18_m: React.CSSProperties;
    Kor_18_r: React.CSSProperties;
    Kor_18_sb: React.CSSProperties;
    Kor_16_b: React.CSSProperties;
    Kor_16_r: React.CSSProperties;
    Kor_14_b: React.CSSProperties;
    Kor_14_r: React.CSSProperties;
  }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    Kor_28_b: true;
    Kor_28_r: true;
    Kor_22_b: true;
    Kor_22_m: true;
    Kor_22_r: true;
    Kor_20_b: true;
    Kor_20_r: true;
    Kor_18_b: true;
    Kor_18_m: true;
    Kor_18_r: true;
    Kor_18_sb: true;
    Kor_16_b: true;
    Kor_16_r: true;
    Kor_14_b: true;
    Kor_14_r: true;
  }
}

export default typography;
